import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import { ArrowLeft, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen tb-auth-bg" data-testid="login-page">
      <PublicHeader />
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-3.5rem)]">
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden">
        <div /> {/* spacer (header took the top slot) */}

        {/* Floating glass blobs for playful depth */}
        <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-violet-400/25 via-blue-300/15 to-transparent blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 -right-16 w-[380px] h-[380px] rounded-full bg-gradient-to-tr from-amber-300/30 via-fuchsia-300/15 to-transparent blur-3xl" />

        <div className="relative flex flex-col items-center gap-6">
          {/* Vertical brand lockup — the full TeeBee mascot + wordmark + Factskoot + tagline */}
          <Logo variant="hero" to={null} className="drop-shadow-[0_10px_30px_rgba(67,56,202,0.18)] tb-float" />

          <div className="tb-glass rounded-3xl p-7 max-w-md w-full">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-violet-700 dark:text-violet-300">
              From a teacher in Bengaluru
            </p>
            <p className="mt-3 font-display text-xl leading-snug">
              &ldquo;TeeBee writes my lesson plans, generates my MCQs and even grades my answer sheets.
              I now sleep before midnight again.&rdquo;
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Priya R., Class 9 Mathematics
            </p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Factskoot · Built for educators worldwide.
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back home
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">
            Welcome back.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to continue your teaching superpowers.
          </p>

          <Button
            onClick={googleSignIn}
            variant="outline"
            className="mt-6 w-full h-11 rounded-full"
            data-testid="login-google-button"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px bg-border flex-1" /> or with email <span className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-full"
              data-testid="login-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            New to TeeBee?{" "}
            <Link to="/signup" className="text-violet-600 hover:underline" data-testid="login-to-signup">
              Create an account
            </Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.2s2.7-6.2 6-6.2c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.3 14.6 2.2 12 2.2 6.5 2.2 2 6.7 2 12.2s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" />
    </svg>
  );
}
